import { inject, Injectable } from '@angular/core';
import { environment } from "../../../../../environments/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { catchError, filter, map, Observable, of } from "rxjs";
import {
  AddCaseWalletRequest,
  AddWalletRequest,
  AmlScanDto,
  CreateCaseRequest,
  CaseDto, CasesResponse,
  CreateReferralLinkRequest,
  InvoiceDto,
  LatestAmlReport,
  Operation,
  PaginationResponse,
  ParamsCreateCase,
  ParamsCreateCaseAddWalletToCase,
  PlanCost,
  PlanSuggestion,
  Referral,
  RegisterUserForm,
  Rental,
  RentResourcesRequest,
  Report,
  ReportData,
  StopWalletTransaction,
  SupportedNetwork,
  TransactionEntity,
  UserEntity,
  Wallet,
  WalletCheckNetworkDto,
  WalletCheckQueueDto,
  WalletEntity,
  WalletWithRentalDto,
  EntityHistoryDto,
  AmlScanEntity,
  ProjectSetting
} from "../models/fraud.models";
import { MockFraudService } from "./mock-fraud.service";

interface ReqReportScanWalletBody {
  wallet: string;
}

@Injectable({
  providedIn: 'root'
})
export class FraudService {
  private _apiUrl: string = environment.apiUrl;
  private _httpClient: HttpClient = inject(HttpClient);
  private _mockFraud: MockFraudService = inject(MockFraudService);

  get isMockData(): boolean {
    return environment.mockData;
  }

  reportScanWallet(body: ReqReportScanWalletBody): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/v1/reports/trial`, body);
  }

  // Operations
  getOperations(): Observable<PaginationResponse<Operation>> {
    return this._httpClient.get<PaginationResponse<Operation>>(`${ this._apiUrl }/api/operations`);
  }

  // Referrals
  getReferrals(): Observable<Referral[]> {
    return this._httpClient.get<Referral[]>(`${ this._apiUrl }/api/referrals`);
  }

  createReferral(body: CreateReferralLinkRequest): Observable<Referral> {
    return this._httpClient.post<Referral>(`${ this._apiUrl }/api/referrals`, body);
  }

  // Rental
  deleteRental(uuid: string): Observable<void> {
    return this._httpClient.delete<void>(`${ this._apiUrl }/api/rental/${uuid}`);
  }

  cancelInvoice(id: string): Observable<any> {
    return this._httpClient.delete<any>(`${ this._apiUrl }/api/invoices/cancel/${ id }`);
  }

  getPendingRental(uuid: string): Observable<Rental | undefined> {
    return this._httpClient.get<Rental | undefined>(`${ this._apiUrl }/api/rental/pending/wallet/${uuid}`)
  }

  validWallet(address: string): Observable<boolean> {
    let params = new HttpParams();
    params = params.append('address', address.toString());
    return this._httpClient.get<boolean>(`${ this._apiUrl }/api/rental/valid/${address}`);
  }

  calculateRentalPlanForWallet(address: string): Observable<PlanSuggestion> {
    if (this.isMockData) {
      return this._mockFraud.calculateRentalPlanForWallet(address);
    }
    return this._httpClient.get<PlanSuggestion>(`${ this._apiUrl }/api/rental/${ address }`);
  }

  calculatePlanCostForWallet(params: { incoming: number, outcoming: number, days?: number }): Observable<PlanCost> {
    return this._httpClient.get<PlanCost>(`${ this._apiUrl }/api/rental/plan-cost`, { params });
  }

  rentResourcesForWallet(body: RentResourcesRequest): Observable<{id: string}> {
    return this._httpClient.post<{id: string}>(`${ this._apiUrl }/api/rental`, body);
  }

  startTrial(address: string): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/rental/startTrial/${ address }`, {});
  }

  activeRentalPlan(uuid: string): Observable<void> {
    return this._httpClient.post<void>(`${ this._apiUrl }/api/rental/${uuid}/activate`, {});
  }

  // Transaction operations
  getTransactionByUuid(uuid: string): Observable<TransactionEntity<LatestAmlReport>> {
    return this._httpClient.get<TransactionEntity>(`${ this._apiUrl }/api/compliance/cluster-transaction/${uuid}`).pipe(
      filter(Boolean),
      map((transaction) => {
        const latestAmlReport = JSON.parse(transaction.latestAmlReport) as LatestAmlReport;
        return {
          ...transaction,
          latestAmlReport
        }
      }),
    )
  }

  // Users
  getUserMe(): Observable<UserEntity> {
    return this._httpClient.get<UserEntity>(`${ this._apiUrl }/api/v1/users/me`);
  }

  getDepositAddress(): Observable<string> {
    return this._httpClient.get(`${ this._apiUrl }/api/v1/users/me/deposit-address`, { responseType: 'text' });
  }

  register(body: RegisterUserForm): Observable<UserEntity> {
    return this._httpClient.post<UserEntity>(`${ this._apiUrl }/api/v1/users/register`, body);
  }

  getUserByInitData(): Observable<UserEntity> {
    return this._httpClient.get<UserEntity>(`${ this._apiUrl }/api/v1/users/me`);
  }

  // Wallet
  getSubscribedWallets(): Observable<WalletWithRentalDto[]> {
    let params = new HttpParams();
    params = params.append('walletType', 'AML_ENERGY');
    return this._httpClient.get<WalletWithRentalDto[]>(`${ this._apiUrl }/api/v1/wallet`, {params});
  }

  addWallet(body: AddWalletRequest): Observable<WalletEntity> {
    return this._httpClient.post<WalletEntity>(`${ this._apiUrl }/api/v1/wallet`, body);
  }

  getWallet(uuid: string): Observable<Wallet> {
    return this._httpClient.get<Wallet>(`${ this._apiUrl }/api/v1/wallet/${ uuid }`);
  }

  patchUpdateWallet(uuid: string, body: Partial<Pick<Wallet, 'address' | 'name'>>): Observable<Wallet> {
    return this._httpClient.put<Wallet>(`${ this._apiUrl }/api/v1/wallet/${ uuid }`, body);
  }

  getInfoWallet(address: string): Observable<Wallet> {
    return this._httpClient.get<Wallet>(`${ this._apiUrl }/api/v1/wallet/address/${ address }`);
  }

  deleteWallet(uuid: string): Observable<void> {
    return this._httpClient.delete<void>(`${ this._apiUrl }/api/v1/wallet/${ uuid }`);
  }

  getLatestReportById(id: number): Observable<Report<ReportData> | undefined> {
    if (this.isMockData) {
      return this._mockFraud.getLatestReportById(id).pipe(map(report => ({
        ...report,
        // data: JSON.parse(report.data)
      })));
    }
    return this._httpClient.get<Report>(`${ this._apiUrl }/api/v1/reports/${ id }`).pipe(
      filter(Boolean),
      map(report => ({
        ...report,
        data: JSON.parse(report.data),
      })), catchError(() => of(undefined)));
  }

  getRentalWallet(uuid: string): Observable<Rental[]> {
    return this._httpClient.get<Rental[]>(`${ this._apiUrl }/api/rental/wallet/${ uuid }`);
  }

  getInvoice(id: string): Observable<InvoiceDto> {
    return this._httpClient.get<InvoiceDto>(`${ this._apiUrl }/api/invoices/${ id }`);
  }

  // Wallet Check
  getCheckWalletsNetworks(): Observable<WalletCheckNetworkDto[]> {
    return this._httpClient.get<WalletCheckNetworkDto[]>(`${ this._apiUrl }/api/v1/wallet-check/networks`);
  }

  getCheckWalletsPending(): Observable<WalletCheckQueueDto> {
    return this._httpClient.get<WalletCheckQueueDto>(`${ this._apiUrl }/api/v1/wallet-check/pending`);
  }

  getCheckWalletsById(id: number): Observable<WalletCheckQueueDto> {
    return this._httpClient.get<WalletCheckQueueDto>(`${ this._apiUrl }/api/v1/wallet-check/${id}`);
  }

  postCheckWallet(form: {address: string, network: string}): Observable<WalletCheckQueueDto> {
    // TODO For version 2.0
    const body = {
      ...form,
      // reportProcess: 'MONITORING_WALLET_CHECK'
    };
    return this._httpClient.post<WalletCheckQueueDto>(`${ this._apiUrl }/api/v1/wallet-check`, body)
  }

  postCheckWalletValidate(body: {address: string, network: string}, doActivenessCheck?: boolean): Observable<void> {
    let params = new HttpParams();
    // if (typeof doActivenessCheck === 'boolean') {
      params = params.append('doActivenessCheck', String(true));
    // }
    return this._httpClient.post<void>(`${ this._apiUrl }/api/v1/wallet-check/validate`, body, { params });
  }

  deleteCheckWalletPending(): Observable<void> {
    return this._httpClient.delete<void>(`${ this._apiUrl }/api/v1/wallet-check/pending`);
  }

  getStopWalletTransactionId(transitionId: string): Observable<AmlScanDto<LatestAmlReport>> {
    return this._httpClient.get<AmlScanDto>(`${ this._apiUrl }/api/compliance/cluster-transaction/${transitionId}`).pipe(
      filter(Boolean),
      map((transaction) => {
        const latestAmlReport = JSON.parse(transaction.latestAmlReport) as LatestAmlReport;
        return {
          ...transaction,
          latestAmlReport
        }
      }),
    );
  }

  postStopWalletTransactionId(transitionId: string, body: {blockType: string, blockedAmount: string, blockExpireAt: Date}): Observable<StopWalletTransaction> {
    return this._httpClient.post<StopWalletTransaction>(`${ this._apiUrl }/api/compliance/cluster-transaction/${transitionId}/block`, body);
  }

  getComplianceBlockedList(): Observable<AmlScanDto[]> {
    return this._httpClient.get<AmlScanDto[]>(`${ this._apiUrl }/api/compliance/cluster-transaction/blocked`);
  }

  getComplianceBlockTypes(): Observable<string[]> {
    return this._httpClient.get<string[]>(`${ this._apiUrl }/api/compliance/block-types`);
  }

  getApiCases(queryParams?: Partial<{
    search: string;
    page: number,
    size: number,
    sort: string
  }>): Observable<PaginationResponse<CaseDto>> {
    let params = new HttpParams({ fromObject: queryParams ?? {} });
    return this._httpClient.get<PaginationResponse<CaseDto>>(`${ this._apiUrl }/api/cases`, { params });
  }

  getSupportedNetworks(): Observable<SupportedNetwork[]> {
    return this._httpClient.get<SupportedNetwork[]>(`${ this._apiUrl }/api/v1/subscriptions/supported-networks`);
  }

  getSubscriptionsWalletBalance(network: string, address: string): Observable<{ [key: string]: number }> {
    return this._httpClient.get<{ [key: string]: number }>(`${ this._apiUrl }/api/v1/subscriptions/wallet-balance/${network}/${address}`);
  }

  getScamTypes(): Observable<{ [key: string]: string }> {
    return this._httpClient.get<{ [key: string]: string }>(`${ this._apiUrl }/api/cases/scam-types`);
  }

  postCase(body: ParamsCreateCase): Observable<CreateCaseRequest> {
    return this._httpClient.post<CreateCaseRequest>(`${ this._apiUrl }/api/cases`, body);
  }

  postCaseAddWalletToCase(caseId: string, body: ParamsCreateCaseAddWalletToCase): Observable<AddCaseWalletRequest> {
    return this._httpClient.post<AddCaseWalletRequest>(`${ this._apiUrl }/api/cases/${caseId}/wallet`, body);
  }

  getHistory(entityType: string, entityId: number): Observable<EntityHistoryDto[]> {
    return this._httpClient.get<EntityHistoryDto[]>(`${ this._apiUrl }/api/history/${entityType}/${entityId}`);
  }

  putSubmitFreezeRequest(caseId: string, amlScanUuid: string, body: {comment: string}): Observable<any> {
    return this._httpClient.put(`${ this._apiUrl }/api/cases/${caseId}/aml-scan/${amlScanUuid}/submit-freeze-request`, body);
  }

  getAmlScanByUuid(uuid: string): Observable<AmlScanEntity<LatestAmlReport>> {
    return this._httpClient.get<AmlScanEntity>(`${ this._apiUrl }/api/aml-scans/${ uuid }`).pipe(
      filter(Boolean),
      map((transaction) => {
        const latestAmlReport = JSON.parse(transaction.latestAmlReport ?? '') as LatestAmlReport;
        return {
          ...transaction,
          latestAmlReport
        }
      }),
    );
  }

  getProjectSetting(): Observable<ProjectSetting> {
    return this._httpClient.get<ProjectSetting>(`${this._apiUrl}/api/v1/project-settings`);
  }
}
